import { createContext, useContext, useEffect, useState } from "react";
import {
  connectMatch as connectMatchAPI,
  matchPaymentToInvoice as matchPaymentToInvoiceAPI,
  matchPayedInvoicesAgainstInvoices,
  matchRemove,
  matchArticle as matchArticleAPI,
  confirmMatches as confirmMatchesAPI,
  collectionPayments as collectionPaymentsAPI,
  matchCreditToInvoice as matchCreditToInvoiceAPI,
  matchSearch as APIMatchSearch,
  matchConnectTwoPayments as APImatchConnectTwoPayments,
  matchExtendedSearch as APIMatchExtendedSearch,
  matchaccount as matchAccountAPI,
  coinRounding as coinRoundingAPI,
  getAllEndCustomerPaidBundles,
  getEndCustomerPaidBundle,
  createfromallfilesBundle,
  deleteEndCustomerPaymentBundle as deleteEndCustomerPaymentBundleAPI,
} from "API/swesum";
import { usePopupNotificationContext } from "./NotificationPopup";

const EndCustomerPaidContext = createContext(null);

export const EndCustomerPaidContextProvider = ({ children }) => {
  const [invoices, setInvoices] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedBundleId, setSelectedBundleId] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);

  const [totalNrConnectedInvoices, setTotalNrConnected] = useState(0);
  const [totalConnectedAmount, setTotalConnectedAmount] = useState(0);
  const [totalAmountLeft, setTotalAmountLeft] = useState(0);
  const [totalNrInvoices, setTotalNrInvoices] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const { openError } = usePopupNotificationContext();

  useEffect(() => {
    setSelectedInvoiceId(null);
    setSelectedBundle(bundles.find((bundle) => bundle.Id === selectedBundleId));
    if (selectedBundleId) {
      fetchInvoices();
    } else {
      setInvoices([]);
    }
  }, [selectedBundleId]);

  useEffect(() => {
    const invoice = invoices.find((i) => i.Id === selectedInvoiceId);
    setSelectedInvoice(invoice);
  }, [selectedInvoiceId, invoices]);

  useEffect(() => {
    let nrConnectedInvoices = 0;
    let totalConnectedAmount = 0;
    let totalAmountLeft = 0;
    let totalAmount = 0;

    for (const invoice of invoices) {
      totalAmountLeft += invoice.AmountLeft;
      totalAmount += invoice.Amount;
      totalConnectedAmount += invoice.ConnectedAmount;
      if (invoice.AmountLeft === 0) {
        nrConnectedInvoices += 1;
      }
    }
    setTotalConnectedAmount(totalConnectedAmount);
    setTotalNrConnected(nrConnectedInvoices);
    setTotalAmountLeft(totalAmountLeft);
    setTotalNrInvoices(invoices.length);
    setTotalAmount(totalAmount);
  }, [invoices]);

  const fetchAllBundles = async () => {
    try {
      const res = await getAllEndCustomerPaidBundles();
      if (res.status === 200 && res.data) {
        setBundles(res.data);
      }
    } catch (_) {}
  };

  const matchPaidInvoicesToInvoices = async () => {
    setLoading(true);
    const res = await matchPayedInvoicesAgainstInvoices();
    if (res.status === 201) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
  };

  const createPaidInvoices = async (ignoreResponse = false) => {
    setLoading(true);
    if (!selectedBundleId) {
      setLoading(false);
      return null;
    }
    try {
      const res = await createfromallfilesBundle(selectedBundleId);
      if (res.status === 201 && res.data.Data && !ignoreResponse) {
        const merged = [
          ...res.data.Data,
          ...invoices.filter(
            (invoice) => !res.data.Data.some((data) => data.Id === invoice.Id)
          ),
        ];
        setInvoices(merged);
      }
      return res;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const confirmMatches = async () => {
    setLoading(true);
    try {
      const res = await confirmMatchesAPI(selectedBundleId);
      setInvoices(res.data.Data);
      collectionPaymentsAPI();
      return res;
    } catch (err) {
      return err.response;
    } finally {
      setLoading(false);
    }
  };

  const matchConnectTwoPayments = async (firstPaymentId, secondPaymentId) => {
    setLoading(true);
    const body = {
      firstPaymentId,
      secondPaymentId,
    };
    try {
      const res = await APImatchConnectTwoPayments(selectedBundleId, body);
      if (res.data.Status.toLowerCase() === "ok") {
        setInvoices(res.data.Data);
      }
      return res;
    } catch (err) {
      return err.response;
    } finally {
      setLoading(false);
    }
  };

  const matchPaymentToInvoice = async (paymentId, invoiceId) => {
    setLoading(true);
    try {
      const body = {
        PaymentId: paymentId,
        InvoiceId: invoiceId,
      };
      const res = await matchPaymentToInvoiceAPI(selectedBundleId, body);
      if (res.status === 201 && res.data.Data) {
        setInvoices(res.data.Data);
      }
      return res;
    } catch (_) {
      return null;
    } finally {
      setLoading(false);
    }
  };

  const connectMatch = async (paidInvoiceId, invoiceId, accountId) => {
    setLoading(true);
    const res = await connectMatchAPI(selectedBundleId, {
      PaidInvoiceId: paidInvoiceId,
      InvoiceId: invoiceId,
      AccountId: accountId,
    });
    if (res.status === 201 && res.data.Data) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
    return res;
  };

  const matchArticle = async (paidInvoiceId, articleId, msg) => {
    setLoading(true);
    const res = await matchArticleAPI(selectedBundleId, {
      PaidInvoiceId: paidInvoiceId,
      ArticleId: articleId,
      Message: msg,
    });
    if (res.status === 201) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
    return res;
  };

  const matchAccount = async (paidInvoiceId, accountId, clientId = null) => {
    setLoading(true);
    const res = await matchAccountAPI(selectedBundleId, {
      PaidInvoiceId: paidInvoiceId,
      AccountId: accountId,
      ClientId: clientId,
    });
    if (res.status === 201) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
    return res;
  };

  const matchSearch = async (paidInvoiceId, OCR) => {
    setLoading(true);
    const res = await APIMatchSearch(paidInvoiceId, OCR);
    if (res.status === 201 && res.data.Data && res.data.Data.length > 0) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
    return res;
  };

  const matchExtendedSearch = async (paidInvoiceId) => {
    setLoading(true);
    const res = await APIMatchExtendedSearch(paidInvoiceId);
    if (res.status === 201 && res.data.Data && res.data.Data.length > 0) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
    return res;
  };

  const matchCreditToInvoice = async (invoiceId, creditId) => {
    setLoading(true);
    const body = {
      InvoiceId: invoiceId,
      CreditId: creditId,
    };
    try {
      const res = await matchCreditToInvoiceAPI(selectedBundleId, body);
      if (res.status === 201 && res.data.Data && res.data.Data.length > 0) {
        setInvoices(res.data.Data);
      }
      return res;
    } catch (err) {
      return err;
    } finally {
      setLoading(false);
    }
  };

  const removeMatch = async (paidInvoiceId, accountId) => {
    setLoading(true);
    const res = await matchRemove(selectedBundleId, {
      PaidInvoiceId: paidInvoiceId,
      AccountId: accountId,
    });
    if (res.status === 201) {
      setInvoices(res.data.Data);
    }
    setLoading(false);
    return res;
  };

  const fetchInvoices = async () => {
    if (selectedBundleId !== null) {
      try {
        const res = await getEndCustomerPaidBundle(selectedBundleId);
        if (res.status === 201 && res.data) {
          setInvoices(res.data.Data);
        }
      } catch (err) {
        setInvoices([]);
        openError(
          `Någonting gick fel vid hämting av bunt: ${selectedBundleId}`
        );
      }
    } else {
      setInvoices([]);
    }
  };

  const coinRounding = async () => {
    setLoading(true);
    const res = await coinRoundingAPI(selectedBundleId);
    setLoading(false);
    return res;
  };

  const deleteBundle = async () => {
    if (!selectedBundleId) return null;
    setLoading(true);
    try {
      const res = await deleteEndCustomerPaymentBundleAPI(selectedBundleId);
      if (res.status === 201) {
        setSelectedBundleId(null);
        setInvoices([]);
        await fetchAllBundles();
      }
      return res;
    } catch (err) {
      return err.response;
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndCustomerPaidContext.Provider
      value={{
        invoices,
        bundles,
        selectedBundleId,
        setSelectedBundleId,
        selectedBundle,
        selectedInvoice,
        setSelectedInvoiceId,
        matchPaidInvoicesToInvoices,
        matchConnectTwoPayments,
        matchSearch,
        matchExtendedSearch,
        matchPaymentToInvoice,
        connectMatch,
        matchArticle,
        matchAccount,
        matchCreditToInvoice,
        confirmMatches,
        createPaidInvoices,
        fetchInvoices,
        fetchAllBundles,
        removeMatch,
        totalAmount,
        totalNrInvoices,
        totalAmountLeft,
        totalNrConnectedInvoices,
        totalConnectedAmount,
        loading,
        coinRounding,
        deleteBundle,
      }}
    >
      {children}
    </EndCustomerPaidContext.Provider>
  );
};

export const useEndCustomerPaid = () => useContext(EndCustomerPaidContext);
